import type {IconProp} from "@atlaskit/button/dist/types/new-button/variants/types";
import DragHandleVerticalIcon from "@atlaskit/icon/core/drag-handle-vertical";
import {token} from "@atlaskit/tokens";
import {twJoin} from "tailwind-merge";

export type ContentBlockDragHandleProps = {
    onPointerDown?: React.PointerEventHandler<HTMLElement>;
    onMouseDown?: React.MouseEventHandler<HTMLElement>;
    onTouchStart?: React.TouchEventHandler<HTMLElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
    tabIndex?: number;
    role?: string;
    "aria-describedby"?: string;
    "aria-pressed"?: boolean;
    "aria-roledescription"?: string;
};

export function EditableContentBlockWrapper({
    children,
    title,
    setTitle,
    onDragStart,
    onDragEnd,
    additonalContent,
    headerTrailingContent,
    isDraggable,
    additionalContentVariant = "badge",
    dragHandleProps,
    containerClassName,
    headerLeadingContent,
    hideTitle,
}: {
    children: React.ReactNode;
    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    onDragStart: () => void;
    onDragEnd: () => void;
    additonalContent?: React.ReactNode;
    /** When set, rendered in header instead of additonalContent (e.g. remove button for media blocks). */
    headerTrailingContent?: React.ReactNode;
    isDraggable: boolean;
    additionalContentVariant?: "badge" | "inline";
    dragHandleProps?: ContentBlockDragHandleProps;
    containerClassName?: string;
    headerLeadingContent?: React.ReactNode;
    hideTitle?: boolean;
}) {
    const nativeDragProps =
        isDraggable === true && dragHandleProps === undefined
            ? {
                  draggable: true,
                  onDragStart: () => onDragStart(),
                  onDragEnd: () => onDragEnd(),
              }
            : {};

    return (
        <div className={containerClassName ?? "w-full bg-white border border-ds-border rounded-lg shadow-sm overflow-hidden"}>
            <div className="grid grid-cols-[max-content_max-content_max-content_1fr_auto] grid-rows-1 items-center gap-ds-space-100 px-ds-space-200 py-ds-space-150 border-b border-ds-border">
                <button
                    type="button"
                    aria-label="Drag to reorder"
                    disabled={isDraggable === false}
                    style={{
                        width: 24,
                        minHeight: 24,
                        padding: "0 var(--space-padding-interactive-large, 12px)",
                        display: "inline-flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "var(--radius-medium, 4px)",
                        border: "none",
                        cursor: isDraggable === true ? "grab" : "default",
                        background:
                            isDraggable === false
                                ? token("color.background.disabled")
                                : token("color.background.neutral.subtle"),
                        color: token("color.icon.subtle"),
                    }}
                    {...nativeDragProps}
                    {...(isDraggable === true ? dragHandleProps : undefined)}
                >
                    <span style={{ width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0 }}>
                        <DragHandleVerticalIcon label="" size="medium" />
                    </span>
                </button>
                <div
                    className="w-[1px] h-full"
                    style={{backgroundColor: token("color.border")}}
                ></div>

                {headerLeadingContent && <div className="flex items-center">{headerLeadingContent}</div>}

                {hideTitle ? (
                    <div className="flex-1" />
                ) : (
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="min-w-0 bg-transparent border-none focus:outline-none focus:ring-0"
                        style={{
                            color: token("color.text"),
                            fontFeatureSettings: "'liga' off, 'calt' off",
                            fontFamily:
                                "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
                            fontSize: "var(--Font-Size-Body-M, 14px)",
                            fontStyle: "normal",
                            fontWeight: "var(--Font-Weight-Body-Medium, 500)",
                            lineHeight: "var(--Line-Height-Body-M, 20px)",
                        }}
                    />
                )}

                {headerTrailingContent !== undefined ? (
                    <div className="justify-self-end">{headerTrailingContent}</div>
                ) : additonalContent !== undefined ? (
                    additionalContentVariant === "badge" ? (
                        <span
                            className="text-ds-font-body-small px-ds-space-100 py-ds-space-050 rounded-full"
                            style={{
                                backgroundColor: token("color.background.accent.green.subtlest"),
                                color: token("color.text.accent.green.bolder"),
                            }}
                        >
                            {additonalContent}
                        </span>
                    ) : (
                        <div className="justify-self-end">{additonalContent}</div>
                    )
                ) : null}
            </div>
            <div className="grid w-full grid-cols-1 grid-rows-1 p-ds-space-200">{children}</div>
        </div>
    );
}

export function ReadOnlyContentBlockWrapper({
    children,
    title,
    additonalContent,
    headerBackgroundColor,
    headerTextColor,
    leftSideIcon,
}: {
    children: React.ReactNode;
    title: string;
    additonalContent?: React.ReactNode;
    headerBackgroundColor?: string;
    headerTextColor?: string;
    leftSideIcon?: IconProp;
}) {
    const Icon = leftSideIcon;
    const hasIcon = Icon !== undefined;
    const hasAdditionalContent = additonalContent !== undefined;

    const gridColsClassName =
        hasIcon === true && hasAdditionalContent === true
            ? "grid-cols-[max-content_max-content_1fr_auto]"
            : hasIcon === true
              ? "grid-cols-[max-content_max-content_1fr]"
              : hasAdditionalContent === true
                ? "grid-cols-[1fr_auto]"
                : "grid-cols-1";

    return (
        <div className="w-full bg-white border border-ds-border rounded-lg shadow-sm overflow-hidden">
            <div
                className={twJoin(
                    "grid grid-rows-1 items-center gap-ds-space-100 px-ds-space-200 py-ds-space-150 border-b border-ds-border",
                    gridColsClassName,
                )}
                style={
                    headerBackgroundColor !== undefined
                        ? {
                              backgroundColor: headerBackgroundColor,
                              color: headerTextColor,
                          }
                        : headerTextColor !== undefined
                          ? {color: headerTextColor}
                          : undefined
                }
            >
                {hasIcon === true ? (
                    <span style={headerTextColor !== undefined ? {color: headerTextColor} : undefined}>
                        <Icon label="" />
                    </span>
                ) : null}
                {hasIcon === true ? (
                    <div
                        className="w-[1px] h-full"
                        style={{
                            backgroundColor:
                                headerTextColor !== undefined ? "rgba(255,255,255,0.5)" : token("color.border"),
                        }}
                    />
                ) : null}
                <span
                    className="truncate"
                    style={{
                        color: token("color.text"),
                        fontFeatureSettings: "'liga' off, 'calt' off",
                        fontFamily:
                            "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
                        fontSize: "var(--Font-Size-Body-M, 14px)",
                        fontStyle: "normal",
                        fontWeight: "var(--Font-Weight-Body-Medium, 500)",
                        lineHeight: "var(--Line-Height-Body-M, 20px)",
                    }}
                >
                    {title}
                </span>
                {hasAdditionalContent === true ? additonalContent : null}
            </div>
            <div className="grid w-full grid-cols-1 grid-rows-1 p-ds-space-200">{children}</div>
        </div>
    );
}
